import dayjs from 'lib/dayjs';
import Icon from '../../../components/AppIcon';
import { formatLeaveTypeDisplay, getEmployeeDisplayName, LEAVE_TYPES } from '../../../utils/common';

const CalendarGrid = ({
  currentDate,
  holidays,
  leaves,
  onDateClick,
  calendarFilters,
  employeeBirthdays = [],
  employeeWorkAnniversaries = [],
}) => {
  const today = new Date();
  today?.setHours(0, 0, 0, 0);

  // Get first day of the month
  const firstDayOfMonth = new Date(currentDate?.getFullYear(), currentDate?.getMonth(), 1);

  // Get the starting day (accounting for the first day of the week)
  const startDay = new Date(firstDayOfMonth);
  startDay?.setDate(startDay?.getDate() - firstDayOfMonth?.getDay());

  // Create calendar grid
  const days = [];
  const current = new Date(startDay);

  // Generate 6 weeks of days
  for (let week = 0; week < 6; week++) {
    for (let day = 0; day < 7; day++) {
      days?.push(new Date(current));
      current?.setDate(current?.getDate() + 1);
    }
  }

  const getDateKey = (date) => {
    return `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}-${String(date?.getDate())?.padStart(2, '0')}`;
  };

  const getEventsForDate = (date) => {
    const dateKey = getDateKey(date);
    const dayEvents = holidays?.[dateKey] || [];

    // Filter events based on calendar filters
    return dayEvents?.filter((event) => {
      switch (event?.type) {
        case 'EVENT_CELEBRATION':
          return calendarFilters?.eventCelebration;
        case 'FULL_HOLIDAY':
          return calendarFilters?.fullHoliday;
        case 'HALF_HOLIDAY':
          return calendarFilters?.halfHoliday;
        case 'SATURDAY_OFF':
          return calendarFilters?.saturdayOff;
        default:
          return true;
      }
    });
  };

  const getLeavesForDate = (date) => {
    const dateKey = getDateKey(date);

    const leave = leaves?.filter((leave) => {
      if (!calendarFilters?.myLeaves || leave?.status !== 'APPROVED') {
        return false;
      }

      // If this specific date is cancelled, don't show it on the calendar
      if (leave?.canceled_dates?.includes(dateKey)) {
        return false;
      }

      // Handle specific dates array (e.g. for multiple day leaves or specific selected dates)
      if (leave?.dates && Array.isArray(leave?.dates) && leave?.dates.length > 0) {
        return leave.dates.some((d) => dayjs(d).format('YYYY-MM-DD') === dateKey);
      }

      // Handle datetime-based leaves
      if (leave?.start_date_time) {
        const startDate = dayjs(leave.start_date_time).format('YYYY-MM-DD');
        const endDate = leave.end_date_time
          ? dayjs(leave.end_date_time).format('YYYY-MM-DD')
          : startDate;

        return dateKey >= startDate && dateKey <= endDate;
      }

      return false;
    });
    return leave;
  };

  const isCurrentMonth = (date) => {
    return date?.getMonth() === currentDate?.getMonth();
  };

  const isToday = (date) => {
    return date?.getTime() === today?.getTime();
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'EVENT_CELEBRATION':
        return 'bg-blue-500';
      case 'FULL_HOLIDAY':
        return 'bg-red-500';
      case 'HALF_HOLIDAY':
        return 'bg-orange-500';
      case 'SATURDAY_OFF':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'EVENT_CELEBRATION':
        return 'Star';
      case 'FULL_HOLIDAY':
        return 'Calendar';
      case 'HALF_HOLIDAY':
        return 'Clock';
      case 'SATURDAY_OFF':
        return 'Home';
      default:
        return 'Circle';
    }
  };

  const getLeaveTypeColor = () => {
    return 'bg-purple-500';
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case LEAVE_TYPES.FULL_DAY:
        return 'Calendar';
      case LEAVE_TYPES.HALF_DAY:
        return 'Clock';
      case LEAVE_TYPES.SHORT:
        return 'Timer';
      case LEAVE_TYPES.LONG_TIME:
        return 'CalendarRange';
      case LEAVE_TYPES.MULTIPLE_DAY:
        return 'CalendarDays';
      default:
        return 'FileText';
    }
  };

  const getEmployeeEventsForDate = (date) => {
    const dateKay = getDateKey(date);
    let birthdays = [];
    let anniversaries = [];

    if (calendarFilters.birthday)
      birthdays = employeeBirthdays
        .filter((e) => getDateKey(new Date(e.date)) == dateKay)
        .map((e) => ({
          name: e.name,
          type: 'Birthday',
          color: 'yellow-600',
        }));

    if (calendarFilters.anniversary)
      anniversaries = employeeWorkAnniversaries
        .filter((e) => getDateKey(new Date(e.date)) == dateKay)
        .map((e) => ({
          name: e.name,
          type: 'Work Anniversary',
          color: 'cyan-600',
        }));

    return [...birthdays, ...anniversaries];
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Calendar Header - Days of Week */}
      <div className="grid grid-cols-7 border-b border-border bg-secondary-50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day, index) => (
          <div
            key={index}
            className="px-2 lg:px-3 py-3 lg:py-4 text-center text-xs lg:text-sm font-medium text-text-secondary border-r border-border last:border-r-0"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day?.charAt(0)}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days?.map((date, index) => {
          const events = getEventsForDate(date);
          const dateLeaves = getLeavesForDate(date);
          const employeeEvents = getEmployeeEventsForDate(date);
          const isInCurrentMonth = isCurrentMonth(date);
          const isTodayDate = isToday(date);
          const hasAnyEvents =
            events?.length > 0 || dateLeaves?.length > 0 || employeeEvents?.length > 0;
          return (
            <div
              key={index}
              onClick={() => onDateClick?.(date, events, dateLeaves, employeeEvents)}
              className={`min-h-[80px] sm:min-h-[100px] lg:min-h-[120px] px-2 lg:px-3 py-2 border-r border-b border-border last:border-r-0 transition-colors duration-200 cursor-pointer hover:bg-secondary-50 ${
                !isInCurrentMonth ? 'bg-secondary-25 text-text-tertiary' : ''
              } ${isTodayDate ? 'bg-primary-50 ring-1 lg:ring-2 ring-primary-200' : ''}`}
            >
              {/* Date Number */}
              <div
                className={`text-xs lg:text-sm font-medium mb-1 lg:mb-2 ${
                  isTodayDate
                    ? 'text-primary-700'
                    : isInCurrentMonth
                      ? 'text-text-primary'
                      : 'text-text-tertiary'
                }`}
              >
                {date?.getDate()}
              </div>

              {/* Events */}
              {events?.length > 0 && (
                <div className="space-y-1 mb-1">
                  {events?.slice(0, 1)?.map((event, eventIndex) => (
                    <div
                      key={eventIndex}
                      className={`flex items-center space-x-1 px-1 lg:px-2 py-0.5 lg:py-1 rounded text-xs text-white truncate ${getEventTypeColor(event?.type)}`}
                      title={`${event?.title} (${event?.type})`}
                    >
                      <Icon
                        name={getEventTypeIcon(event?.type)}
                        size={8}
                        className="lg:w-[10px] lg:h-[10px]"
                      />
                      <span className="truncate text-xs">{event?.title}</span>
                    </div>
                  ))}
                  {events?.length > 1 && (
                    <div className="text-xs text-text-secondary px-1 lg:px-2 py-0.5 lg:py-1 bg-secondary-100 rounded">
                      +{events?.length - 1} more
                    </div>
                  )}
                </div>
              )}
              {employeeEvents?.length > 0 && (
                <div className="space-y-1">
                  {employeeEvents?.slice(0, 2)?.map((event, leaveIndex) => (
                    <div
                      key={leaveIndex}
                      className={`flex items-center space-x-1 px-1 lg:px-2 py-0.5 lg:py-1 rounded text-xs text-white truncate ${event.type == 'Birthday' ? 'bg-yellow-600' : 'bg-cyan-600'}`}
                      title={`${event.name} - ${event.type}`}
                    >
                      <Icon name={'Cake'} size={8} className="lg:w-[10px] lg:h-[10px]" />
                      <span className="truncate text-xs">
                        {event.name} - {event.type}
                      </span>
                    </div>
                  ))}
                  {employeeEvents?.length > 2 && (
                    <div className="text-xs text-text-secondary px-1 lg:px-2 py-0.5 lg:py-1 bg-purple-100 rounded">
                      +{employeeEvents?.length - 2} more
                    </div>
                  )}
                </div>
              )}

              {/* Leaves */}
              {dateLeaves?.length > 0 && (
                <div className="space-y-1">
                  {dateLeaves?.slice(0, 2)?.map((leave, leaveIndex) => (
                    <div
                      key={leaveIndex}
                      className={`flex items-center space-x-1 px-1 lg:px-2 py-0.5 lg:py-1 rounded text-xs text-white truncate ${getLeaveTypeColor(leave?.type)}`}
                      title={`${getEmployeeDisplayName(leave)} - ${formatLeaveTypeDisplay(leave)}`}
                    >
                      <Icon
                        name={getLeaveTypeIcon(leave?.type)}
                        size={8}
                        className="lg:w-[10px] lg:h-[10px]"
                      />
                      <span className="truncate text-xs">{getEmployeeDisplayName(leave)}</span>
                    </div>
                  ))}
                  {dateLeaves?.length > 2 && (
                    <div className="text-xs text-text-secondary px-1 lg:px-2 py-0.5 lg:py-1 bg-purple-100 rounded">
                      +{dateLeaves?.length - 2} more
                    </div>
                  )}
                </div>
              )}

              {/* Empty State for Current Month */}
              {!hasAnyEvents && isInCurrentMonth && (
                <div className="flex items-center justify-center h-full opacity-30">
                  <div className="text-xs text-text-tertiary flex items-center space-x-1">
                    <Icon name="Calendar" size={10} className="lg:w-3 lg:h-3" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;
